/**
 * useUrlState — bi-directional React state ⇄ URL query-string sync.
 *
 * Source of truth is the URL. A schema of codecs tells the hook how to parse
 * each query param into a typed value and stringify it back. Defaults are
 * omitted from the URL so the address bar stays clean. Writes use
 * `replace: true` so filter/search changes do not pollute browser history.
 *
 * Text inputs can opt-in to debounced URL writes while still reflecting
 * keystrokes immediately in the UI (mirror + flush pattern).
 *
 * IMPORTANT: The `config` object must be stable across renders. Define it
 * as a module-level constant or wrap it in `useMemo(..., [])`. Changing the
 * config on every render defeats downstream `useMemo` stability.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

export type Codec<T> = {
  /** Parse a raw query-string value (or null when absent) into a typed value. */
  parse: (raw: string | null) => T;
  /**
   * Stringify a typed value for the URL. Return `null` to OMIT the param from
   * the URL entirely (used for default values and empty strings).
   */
  stringify: (v: T) => string | null;
  /** If set, `set()` calls for this key debounce the URL write by this many ms. */
  debounceMs?: number;
};

export type UrlStateConfig<S extends Record<string, any>> = {
  [K in keyof S]: Codec<S[K]>;
};

export type UrlStateReturn<S extends Record<string, any>> = {
  /** Typed record of the current state (URL + any debounced pending values). */
  state: S;
  /** Update a single key. Debounced if the codec specifies debounceMs. */
  set: <K extends keyof S>(key: K, value: S[K]) => void;
  /** Atomic multi-key update (single URL write, single history entry). */
  patch: (partial: Partial<S>) => void;
  /** Reset given keys (or all keys) back to their defaults. */
  reset: (keys?: (keyof S)[]) => void;
};

/* ────────────────────────────────────────────────────────────────────────── */
/*  Codec helpers — small factories for the common param shapes.              */
/* ────────────────────────────────────────────────────────────────────────── */

/** Plain string, defaulting to empty. Empty string = omitted from URL. */
export function stringCodec(
  defaultValue: string = '',
  opts?: { debounceMs?: number },
): Codec<string> {
  return {
    parse: (raw) => (raw == null ? defaultValue : raw),
    stringify: (v) => (v === defaultValue || v === '' ? null : v),
    debounceMs: opts?.debounceMs,
  };
}

/** Nullable string — both `null` and `''` are treated as "not set" and omitted. */
export function nullableStringCodec(): Codec<string | null> {
  return {
    parse: (raw) => (raw && raw.length > 0 ? raw : null),
    stringify: (v) => (v == null || v === '' ? null : v),
  };
}

/** Integer, with a default that is omitted from the URL. */
export function intCodec(defaultValue: number): Codec<number> {
  return {
    parse: (raw) => {
      if (raw == null) return defaultValue;
      const n = parseInt(raw, 10);
      return Number.isFinite(n) ? n : defaultValue;
    },
    stringify: (v) => (v === defaultValue ? null : String(v)),
  };
}

/** Boolean using `1`/`0` in the URL. Default value is omitted. */
export function boolCodec(
  defaultValue: boolean,
  trueStr: string = '1',
  falseStr: string = '0',
): Codec<boolean> {
  return {
    parse: (raw) => {
      if (raw == null) return defaultValue;
      if (raw === trueStr || raw === 'true' || raw === 'yes') return true;
      if (raw === falseStr || raw === 'false' || raw === 'no') return false;
      return defaultValue;
    },
    stringify: (v) => (v === defaultValue ? null : v ? trueStr : falseStr),
  };
}

/** One-of enum. Values outside the allow-list fall back to the default. */
export function enumCodec<T extends string>(
  values: readonly T[],
  defaultValue: T,
): Codec<T> {
  return {
    parse: (raw) => {
      if (raw != null && (values as readonly string[]).includes(raw)) {
        return raw as T;
      }
      return defaultValue;
    },
    stringify: (v) => (v === defaultValue ? null : v),
  };
}

/**
 * `days` param: int in state, but `0` is serialized as `'all'` for readability.
 * Also accepts `'0'` inbound so old deep-links still work.
 */
export function daysCodec(defaultValue: number = 30): Codec<number> {
  return {
    parse: (raw) => {
      if (raw == null) return defaultValue;
      if (raw === 'all' || raw === '0') return 0;
      const n = parseInt(raw, 10);
      return Number.isFinite(n) && n > 0 ? n : defaultValue;
    },
    stringify: (v) => {
      if (v === defaultValue) return null;
      return v === 0 ? 'all' : String(v);
    },
  };
}

/**
 * Comma-separated code list, e.g. `?j=EU,US`. Values are trimmed, upper-cased
 * and de-duplicated; an empty list falls back to the default, which is omitted
 * from the URL.
 */
export function csvCodec(defaultValue: string[]): Codec<string[]> {
  const same = (a: string[], b: string[]) => a.length === b.length && a.every((v, i) => v === b[i]);
  return {
    parse: (raw) => {
      if (raw == null) return defaultValue;
      const list = Array.from(new Set(raw.split(',').map((s) => s.trim().toUpperCase()).filter(Boolean)));
      return list.length > 0 ? list : defaultValue;
    },
    stringify: (v) => (v.length === 0 || same(v, defaultValue) ? null : v.join(',')),
  };
}

/**
 * Page number: 1-indexed in the URL (human-readable), 0-indexed in state.
 * Default page 0 is omitted.
 */
export function pageCodec(defaultValue: number = 0): Codec<number> {
  return {
    parse: (raw) => {
      if (raw == null) return defaultValue;
      const n = parseInt(raw, 10);
      return Number.isFinite(n) && n > 0 ? n - 1 : defaultValue;
    },
    stringify: (v) => (v === defaultValue ? null : String(v + 1)),
  };
}

/* ────────────────────────────────────────────────────────────────────────── */
/*  Hook                                                                      */
/* ────────────────────────────────────────────────────────────────────────── */

export function useUrlState<S extends Record<string, any>>(
  config: UrlStateConfig<S>,
): UrlStateReturn<S> {
  const [searchParams, setSearchParams] = useSearchParams();

  /* Current URL-derived state. Stable identity as long as searchParams is. */
  const urlState = useMemo<S>(() => {
    const out = {} as S;
    for (const key in config) {
      out[key] = config[key].parse(searchParams.get(key));
    }
    return out;
  }, [searchParams, config]);

  /* Debounced values waiting to be flushed to the URL. */
  const pendingRef = useRef<Partial<S>>({});
  const timersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const [pendingTick, setPendingTick] = useState(0);

  /* Apply a partial update to the URL atomically. */
  const applyUpdate = useCallback(
    (updates: Partial<S>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const key in updates) {
            const codec = config[key];
            if (!codec) continue;
            const str = codec.stringify(updates[key] as S[typeof key]);
            if (str == null) next.delete(key);
            else next.set(key, str);
          }
          return next;
        },
        { replace: true },
      );
    },
    [config, setSearchParams],
  );

  /* Merge URL-derived state with any pending (debounced) overrides. */
  const state = useMemo<S>(() => {
    const pending = pendingRef.current;
    if (Object.keys(pending).length === 0) return urlState;
    return { ...urlState, ...pending } as S;
    // pendingTick forces a re-merge when pendingRef mutates
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlState, pendingTick]);

  const set = useCallback(
    <K extends keyof S>(key: K, value: S[K]) => {
      const codec = config[key];
      const debounceMs = codec?.debounceMs ?? 0;

      if (debounceMs > 0) {
        // Mirror value immediately so the UI is responsive
        pendingRef.current = { ...pendingRef.current, [key]: value };
        setPendingTick((t) => t + 1);

        // Debounce the URL write
        const keyStr = key as string;
        if (timersRef.current[keyStr]) {
          clearTimeout(timersRef.current[keyStr]);
        }
        timersRef.current[keyStr] = setTimeout(() => {
          const pending = pendingRef.current[key];
          const nextPending = { ...pendingRef.current };
          delete nextPending[key];
          pendingRef.current = nextPending;
          delete timersRef.current[keyStr];
          // Computed-key inference is too loose for Partial<S>; go through unknown.
          applyUpdate({ [key]: pending } as unknown as Partial<S>);
          setPendingTick((t) => t + 1);
        }, debounceMs);
      } else {
        applyUpdate({ [key]: value } as unknown as Partial<S>);
      }
    },
    [config, applyUpdate],
  );

  const patch = useCallback(
    (partial: Partial<S>) => {
      // Multi-key updates bypass debouncing — they go straight to the URL.
      // Clear any pending timer/value for the keys we're overwriting.
      for (const key in partial) {
        if (timersRef.current[key]) {
          clearTimeout(timersRef.current[key]);
          delete timersRef.current[key];
        }
        if (key in pendingRef.current) {
          const next = { ...pendingRef.current };
          delete next[key as keyof S];
          pendingRef.current = next;
        }
      }
      applyUpdate(partial);
      setPendingTick((t) => t + 1);
    },
    [applyUpdate],
  );

  const reset = useCallback(
    (keys?: (keyof S)[]) => {
      const keysToReset = keys ?? (Object.keys(config) as (keyof S)[]);
      const update = {} as Partial<S>;
      for (const key of keysToReset) {
        // parse(null) yields the codec's declared default
        update[key] = config[key].parse(null);
      }
      patch(update);
    },
    [config, patch],
  );

  /* Flush any pending debounced writes on unmount (last-char-dropped guard). */
  useEffect(() => {
    return () => {
      for (const k in timersRef.current) {
        clearTimeout(timersRef.current[k]);
      }
      if (Object.keys(pendingRef.current).length > 0) {
        applyUpdate(pendingRef.current as Partial<S>);
      }
    };
  }, [applyUpdate]);

  return { state, set, patch, reset };
}
